import styled from 'styled-components'
import breakpoints from '../../../../config/Breakpoints'
import CardTitle from '../../../../common/styles/CardTitle'
import useBookStatisticsLanguage from '../hooks/useBookStatisticsLanguage'
import LoadingBar from '../../../../common/LoadingBar'
import Spacer from '../../../../common/Spacer'
import booksitoutIcon from '../../../../config/BooksitoutIcon'
import {useNavigate} from 'react-router-dom'
import {BOOK_LANGUAGES, BookLanguage, getBookLanguageDisplayName, getBookLanguageFlag} from '../../BookType'
import BookStatisticsLanguageResponse from '../responses/BookStatisticsLanguageResponse'

// 책이 있는 언어가 1개 이하일 때 같이 보여줄 언어: 한국어 + 전 세계 사용자 수 상위 언어 (Ethnologue 기준, 지원하지 않는 벵골어 제외)
const COMMON_LANGUAGES: BookLanguage[] = ['KOREAN', 'ENGLISH', 'CHINESE', 'HINDI', 'SPANISH', 'FRENCH', 'ARABIC', 'PORTUGUESE', 'RUSSIAN', 'URDU']

const getLanguageName = (language: BookLanguage): string => language === 'UNKNOWN' ? '알 수 없음' : getBookLanguageDisplayName(language)

const BookStatisticsLanguageCard = () => {
	const navigate = useNavigate()
	const [languageStatistics, totalCount, isLoading] = useBookStatisticsLanguage()

	const getPercent = (count: number | undefined, totalCount: number): string => {
		const validCount = count ?? 0
		const percentageRaw = totalCount > 0 ? (validCount / totalCount) * 100 : 0
		const precision = percentageRaw < 1 ? 1 : 0
		const percentage = percentageRaw.toFixed(precision)

		if (validCount === 0) {
			return ''
		}

		return `${percentage}%`
	}

	const getCount = (language: BookLanguage): number | undefined =>
		languageStatistics?.[language.toLowerCase() as keyof BookStatisticsLanguageResponse]

	// 책이 있는 언어가 2개 이상이면 그 언어들만, 1개 이하면 그 언어를 맨 위에 두고 COMMON_LANGUAGES로 10개 채우기
	// 로딩 중에는 언어가 몇 개일지 모르니 3줄만 (작은 화면에서 카드 높이가 내용에 맞춰 줄어들기 때문에 로딩 후 높이 차이를 줄임)
	const presentLanguages = BOOK_LANGUAGES.filter(language => (getCount(language) ?? 0) > 0)
	const languages = isLoading
		? COMMON_LANGUAGES.slice(0, 3)
		: presentLanguages.length >= 2
			? presentLanguages
			: [...presentLanguages, ...COMMON_LANGUAGES.filter(language => !presentLanguages.includes(language))].slice(0, 10)

	const rows = languages.map(language => ({
		language: language,
		name: getLanguageName(language),
		value: getCount(language)?.toString() ?? '-',
		extraValue: getPercent(getCount(language), totalCount),
	})).sort((a, b) => parseFloat(b.value) - parseFloat(a.value))

	return (
		<Container>
			<CardTitle icon={null} mb={0} title={'언어별 독서 요약'} url={''} textSize={5}/>

			<Table>
				<tbody>
				{rows.map(row => {
					const hasBooks = row.value !== '-' && row.value !== '0'
					return (
						<RowLink key={row.language} onClick={hasBooks ? () => navigate(`/book/statistics/languages/${row.language}`) : undefined} $clickable={hasBooks}>
							<th className={'col-7 h5'} style={{color: 'var(--text-color)'}}>
								<LanguageFlag>{getBookLanguageFlag(row.language)}</LanguageFlag>
								{row.name}
							</th>
							<td className={'col-5'} style={{color: 'var(--text-color)'}}>
								<h5 className={'d-flex flex-row'} style={{margin: 0, height: '32px', alignItems: 'center'}}>
									{isLoading ? (
										<div style={{width: '100%'}}>
											<LoadingBar size={3}/>
										</div>
									) : (
										<>
											<CountValue>{row.value}</CountValue>
											{row.extraValue && (
												<ExtraValue>{row.extraValue}</ExtraValue>
											)}
											{hasBooks && (
												<>
													<Spacer/>
													<ChevronIcon>
														<booksitoutIcon.ArrowRight/>
													</ChevronIcon>
												</>
											)}
										</>
									)}
								</h5>
							</td>
						</RowLink>
					)
				})}
				</tbody>
			</Table>
		</Container>
	)
}

const Container = styled.div`
	height: 100%;
	min-height: 650px;
	background-color: var(--card-bg-color);
	border-radius: 16px;
	border: 1px solid rgba(0, 0, 0, 0.06);
	box-shadow: 0 0 10px 0 rgba(0, 0, 0, 0.04);
	padding: 25px;

	@media (max-width: ${breakpoints.md}) {
		height: auto;
		min-height: 0;
	}
`

const Table = styled.table`
	margin: 0;
	width: 100%;

	tbody {
		background-color: transparent !important;
	}

	th, td {
		border-bottom: 1px solid var(--hover-bg-color);
		padding: 10px 8px;
	}
`

const LanguageFlag = styled.span.attrs({
	className: 'me-3',
})`
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 30px;
	height: 30px;
	font-size: 24px;
	line-height: 1;
`

const CountValue = styled.span`
	min-width: 40px;
	text-align: right;
	margin-right: 5px;
`

const ExtraValue = styled.div`
	padding-top: 7px;
	margin: 0;
	font-size: 14px;
	color: var(--sub-text-color);
	min-width: 45px;
	text-align: right;
`

const RowLink = styled.tr<{ $clickable: boolean }>`
	cursor: ${({$clickable}) => $clickable ? 'pointer' : 'default'};

	&:hover {
		background-color: ${({$clickable}) => $clickable ? 'var(--hover-bg-color)' : 'transparent'};
	}
`

const ChevronIcon = styled.div`
	display: flex;
	align-items: center;
	color: var(--sub-text-color);
	font-size: 14px;
`

export default BookStatisticsLanguageCard
